import React, { useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { AcceptDeclineForm } from '../../../_lib/Form2';
import { useDelayed } from '../../../hooks/useDelayed';
import type { Ticket as TicketType } from './Ticket';
import { Ticket } from './Ticket';
import { ModalInformation } from './TicketButton';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { useRefreshView } from '../../../_lib/ViewFetcher';
import { SkipLink, SkipLinkTarget } from '../../../_lib/components/SkipLink';
import { Hint } from '../../../_lib/components/Hint';
import { Tile, TileGrid } from '../../../_lib/components/Tiles';

type TicketsProps = { tickets: TicketType[] };

export function Tickets({ tickets }: TicketsProps) {
  const { t } = useSonTranslation('TicketBooking');
  const skipLinkId = useHtmlValidId();
  const { refreshView } = useRefreshView();
  const [currentModal, setCurrentModal] = useState<ModalInformation>(null);

  const closeModal = () => {
    setCurrentModal(null);
  };
  const delayedCloseModal = useDelayed(closeModal);

  if (tickets.length === 0) {
    return null;
  }

  return (
    <div className="ticket-booking">
      <Heading className="invisible ticket-booking__focusable-heading" hierarchy={4} tabIndex={-1}>
        {t('default.ticketsLabel')}
      </Heading>
      <SkipLink id={`skip-tickets-${skipLinkId}`}>{t('default.skipLinkText')}</SkipLink>
      <TileGrid>
        {tickets.map(ticket => {
          return (
            <Tile key={ticket.ticketId}>
              <Ticket ticket={{ ...ticket }} openModal={setCurrentModal} />
            </Tile>
          );
        })}
      </TileGrid>
      <SkipLinkTarget targetName={t('default.skipLinkTargetText')} id={`skip-tickets-${skipLinkId}`} />
      {currentModal ? (
        <ModalDialog
          closeModal={() => {
            refreshView();
            closeModal();
          }}
          headline={currentModal.headline}
          initialFocusIndex={1}
        >
          {currentModal.action ? (
            <AcceptDeclineForm
              headline={currentModal.question}
              form={currentModal.action}
              messageSuccess={currentModal.messageSuccess}
              messageSending={currentModal.messageSending}
              messageFail={currentModal.messageFail}
              customErrorTypeMessages={
                currentModal.ticketAlreadyBooked ? { ticketAlreadyBooked: currentModal.ticketAlreadyBooked } : undefined
              }
              onSuccess={() => {
                refreshView();
                delayedCloseModal();
              }}
            >
              {currentModal.hint ? <Hint message={currentModal.hint} /> : null}
              {currentModal.children || null}
            </AcceptDeclineForm>
          ) : (
            currentModal.children || null
          )}
        </ModalDialog>
      ) : null}
    </div>
  );
}
